// src/forms/html_element_helpers/error_tracking.js

import { fetch_with_timeout } from '../../helpers';
import { v4 as uuidv4 } from 'uuid';
import merge from 'lodash.merge';

import { build_data_hub_action_privacy_headers } from '../../helpers/data_hub_privacy_policy';
import { build_web_event_request_body, WEB_ANALYTICS_POST_PATH } from '../../analytics/delivery';
import { build_base_analytics_event } from '../../analytics/session';

const default_maximum_error_tracking_requests_per_page = 3;
// Match the analytics flush timeout; error telemetry is diagnostic and fire-and-forget,
// so a slow/cold action backend should never block the form.
const ERROR_TELEMETRY_POST_TIMEOUT_MS = 8000;
// Fallback bucket key for callers that don't supply one (e.g. internal callers without
// an element instance). Internal-only — never sent to the backend.
const SHARED_ERROR_TRACKING_BUCKET_KEY = '__shared__';


// TODO 260426 update so it is true error track. pass the full stack trace. post dog aligned

/**
 * Maps a free-form internal_error_message to a stable, groupable error_group string so
 * BigQuery dashboards can GROUP BY one value instead of 4-5 message variants per root
 * cause (e.g. every ActBlue load/config failure → 'actblue_load'). Message text stays
 * intact in internal_error_message for debugging; this is purely an aggregation key.
 * Defensive: any non-string / unknown input returns 'other'.
 *
 * First-match wins, so order patterns most-specific-first.
 *
 * @param {string} internal_error_message
 * @returns {string} snake_case group key
 */
export const classify_error_group = (internal_error_message) => {
	if (typeof internal_error_message !== 'string' || internal_error_message.length === 0) {
		return 'other';
	}
	const error_group_patterns = [
		{ group: 'actblue_load', pattern: /actblue/i },
		{ group: 'organization_metadata_fetch', pattern: /(organization|client)[\s_]metadata/i },
		{ group: 'user_jwt_fetch', pattern: /user_jwt|jwt/i },
		{ group: 'form_validation', pattern: /missing required fields|invalid email|invalid phone/i },
		{ group: 'form_submit', pattern: /submitting form|post_user_data|submission/i },
		{ group: 'iterable', pattern: /iterable/i },
	];
	const matched = error_group_patterns.find(({ pattern }) => pattern.test(internal_error_message));
	return matched ? matched.group : 'other';
};

/**
 * Per-bucket rate limit state. Previously the counter lived directly on data_hub_instance
 * (a singleton-ish window.data_hub), so two <data-hub-signup-form> elements on the same page
 * shared one quota — first form's errors blew the limit, second form's errors silently dropped.
 *
 * Now keyed by an opaque bucket key (typically the element instance's element_uuid). Two
 * elements with the same explicit data-hub-form-id still get separate quotas, and the bucket
 * key is internal — never logged or sent to the backend.
 *
 * @param {Object} data_hub_instance
 * @param {string} bucket_key
 * @param {number} maximum_error_tracking_requests_per_page
 * @returns {{count: number, in_flight: boolean, max: number}|null}
 */
const get_rate_limited_error_tracking_state = (
	data_hub_instance,
	bucket_key,
	maximum_error_tracking_requests_per_page = default_maximum_error_tracking_requests_per_page
) => {
	if (!data_hub_instance || typeof data_hub_instance !== 'object') {
		return null;
	}

	if (!(data_hub_instance.error_tracking_state_by_bucket instanceof Map)) {
		data_hub_instance.error_tracking_state_by_bucket = new Map();
	}

	const key = (typeof bucket_key === 'string' && bucket_key.length > 0)
		? bucket_key
		: SHARED_ERROR_TRACKING_BUCKET_KEY;
	let state = data_hub_instance.error_tracking_state_by_bucket.get(key);
	if (!state) {
		state = { count: 0, in_flight: false, max: maximum_error_tracking_requests_per_page };
		data_hub_instance.error_tracking_state_by_bucket.set(key, state);
	}
	return state;
};

/**
 * Tracks errors by POSTing a single `error` event to the web-analytics endpoint
 * (`/api/post/web`) — the exact same wire shape, identity, and delivery path as a normal
 * web event (build_base_analytics_event → build_web_event_request_body).
 *
 * Why /api/post/web and NOT /api/post/user_data: the person endpoint runs person-format
 * and writes the person record (person_unprocessed / person_change_log) from the request
 * body's user_data. Error telemetry used to ship the full in-memory user_data snapshot
 * there — including email/cell_phone — so data-hub-action's anonymous-only gate was
 * bypassed and a stale identity snapshot (e.g. captured mid-edit, before a name change had
 * merged) could clobber the person. The web endpoint is events-only: it logs to the
 * data_hub.web BQ table, never runs person-format, and won't mirror `error` to Iterable.
 *
 * Identity (global_uuid / global_uuid_source / uuids) is carried EXACTLY like every other
 * web event: in the JWT on the request body (build_web_event_request_body sends `jwt`).
 * data-hub-action decodes it and stamps the identity columns server-side — no loose person
 * fields are sent (matching delivery.js's "omit loose user_data" contract).
 *
 * @param {Object} params - Named parameters.
 * @param {string} params.internal_error_message - Internal error message for debugging.
 * @param {string} params.external_error_message - User-facing error message.
 * @param {Object} [params.data_hub_instance=window?.data_hub] - The data_hub instance.
 * @param {Object} [params.user_data={}] - Accepted for backward compatibility but NOT sent.
 *   Error telemetry no longer ships a person snapshot (see endpoint note above).
 * @param {Object} [params.metadata={}] - Page/form metadata merged into the event.
 * @param {Object} [params.custom_error_data={}] - Additional error-specific data.
 * @param {Function} [params.error_function] - Error callback for tracking failures (optional, won't throw).
 *
 * @returns {Promise<Object|null>} Parsed response when OK, else null. Resolves on failure too.
 */
export async function track_error_to_action_endpoint({
	internal_error_message,
	external_error_message,
	data_hub_instance = window?.data_hub,
	user_data = {},
	metadata = {},
	custom_error_data = {},
	error_function = null
}) {
	// `user_data` is intentionally unused — kept in the signature so existing callers
	// don't break, but a person snapshot is deliberately never sent (see JSDoc).
	void user_data;

	// Don't track if no data_hub_instance or forms_domain
	if (!data_hub_instance || !data_hub_instance.forms_domain) {
		if (error_function) {
			error_function?.('Error tracking skipped: no data_hub_instance or forms_domain', null);
		}
		return null;
	}

	// Automated browsers (navigator.webdriver = headless Chrome, Selenium, Playwright)
	// generate init-time errors at scale on high-traffic pages and aren't worth a POST.
	// console.error already happened in the caller, so nothing is lost locally.
	try {
		if (typeof navigator !== 'undefined' && navigator.webdriver === true) {
			return null;
		}
	} catch (_) { /* tolerate exotic environments — keep tracking */ }

	try {
		// error_group is the stable aggregation key (also set as event_sub_type so BQ can
		// GROUP BY without parsing web_event_data).
		const error_group = classify_error_group(internal_error_message);

		// Build the event through the SAME canonical builder normal web events use, so the
		// error row carries identical identifier fields (web_event_uuid, session_id,
		// window_id, pageview_id, event_sequence, client_time, browser/metadata snapshot).
		// PostHog models error/exception telemetry as `$exception` (there is no `$error`);
		// the $-prefix also matches every other system event we emit ($pageview, $click, …).
		let error_event = build_base_analytics_event(data_hub_instance, '$exception', {
			event_sub_type: error_group,
			metadata: metadata || {},
			web_event_data: {
				...custom_error_data,
				source: custom_error_data?.source || 'data_hub_form',
				internal_error_message: internal_error_message ?? null,
				external_error_message: external_error_message ?? null,
				error_group,
				error_timestamp: new Date().toISOString()
			}
		});

		// build_base_analytics_event returns null only for an unusable instance (no
		// analytics_state). Fall back to a minimal event so a real error is never dropped.
		if (!error_event) {
			error_event = {
				event_name: '$exception',
				event_sub_type: error_group,
				web_event_uuid: uuidv4(),
				client_time: new Date().toISOString(),
				metadata: merge({}, data_hub_instance?.page_metadata || {}, metadata || {}),
				web_event_data: {
					...custom_error_data,
					source: custom_error_data?.source || 'data_hub_form',
					internal_error_message: internal_error_message ?? null,
					external_error_message: external_error_message ?? null,
					error_group,
					error_timestamp: new Date().toISOString()
				}
			};
		}

		// Reuse the canonical web-event body builder (organization_id, jwt, browser,
		// metadata, schema version). The jwt is the identity source — data-hub-action
		// decodes global_uuid / uuids from it and stamps them on the data_hub.web row.
		const request_body = build_web_event_request_body(data_hub_instance, [error_event], 'error_fetch');

		const error_tracking_api_url = `${data_hub_instance.forms_domain}${WEB_ANALYTICS_POST_PATH}`;

		const headers = {
			'Content-Type': 'application/json',
			...build_data_hub_action_privacy_headers(data_hub_instance),
		};

		// Fire and forget — abort_on_timeout:false so a slow backend surfaces a timeout to
		// the .catch without us canceling a POST the server may still complete.
		const response = await fetch_with_timeout(error_tracking_api_url, {
			method: 'POST',
			headers: headers,
			credentials: 'include',
			body: JSON.stringify(request_body),
			timeout: ERROR_TELEMETRY_POST_TIMEOUT_MS,
			abort_on_timeout: false
		}).catch(error => {
			// Silently fail - we don't want error tracking to cause additional errors
			if (error_function) {
				error_function?.('Error tracking POST failed (non-blocking): ' + error, null);
			}
			return null;
		});

		if (response?.ok) {
			try {
				return await response.json();
			} catch (_) {
				return null;
			}
		}
		return null;

	} catch (error) {
		// Silently fail - we don't want error tracking to cause additional errors
		if (error_function) {
			error_function?.('Error in track_error_to_action_endpoint (non-blocking): ' + error, null);
		}
		return null;
	}
}

/**
 * Logs an error to the console and tracks it to the action endpoint with per-instance rate limiting.
 *
 * @param {Object} params
 * @param {string} params.internal_error_message
 * @param {string} params.external_error_message
 * @param {Object} [params.data_hub_instance=window?.data_hub]
 * @param {Object} [params.user_data]
 * @param {Object} [params.metadata]
 * @param {Object} [params.custom_error_data]
 * @param {number} [params.maximum_error_tracking_requests_per_page=3]
 * @returns {Promise<Object|null>}
 */
export async function log_console_error_and_track_to_action_endpoint_with_rate_limit({
	internal_error_message = null,
	external_error_message = null,
	data_hub_instance = window?.data_hub,
	user_data = data_hub_instance?.user_data || {},
	metadata = data_hub_instance?.page_metadata || {},
	custom_error_data = {},
	rate_limit_bucket_key = null,
	maximum_error_tracking_requests_per_page = default_maximum_error_tracking_requests_per_page
} = {}) {
	console.error(internal_error_message, external_error_message);

	if (!data_hub_instance || typeof data_hub_instance !== 'object' || !data_hub_instance.forms_domain) {
		return null;
	}

	const state = get_rate_limited_error_tracking_state(
		data_hub_instance,
		rate_limit_bucket_key,
		maximum_error_tracking_requests_per_page
	);

	if (!state || state.count >= state.max || state.in_flight) {
		return null;
	}

	state.count += 1;
	state.in_flight = true;

	return track_error_to_action_endpoint({
		internal_error_message,
		external_error_message,
		data_hub_instance,
		user_data,
		metadata,
		custom_error_data,
		error_function: null
	}).then((response_data) => {
		const returned_jwt = response_data?.returns?.jwt_cookie || response_data?.jwt_cookie;
		if (returned_jwt && !data_hub_instance.jwt_cookie) {
			data_hub_instance.jwt_cookie = returned_jwt;
		}
		return response_data;
	}).catch(() => {
		return null;
	}).finally(() => {
		state.in_flight = false;
	});
}

